import { LoadingSpinner } from "@drs-ops/ui/feedback";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RatesGroup } from "@/components/settings/RatesGroup";
import { useAdminApi } from "@/hooks/useAdminApi";
import type { Rate } from "@/types/rate";

const LABOR_KEYS = ["labor_avoid", "labor_professional", "labor_educational", "labor_preferred"] as const;
const PER_DIEM_KEYS = ["per_diem_daily"] as const;
const AIRFARE_KEYS = ["airfare_default"] as const;
const DRIVING_KEYS = ["mileage_per_mile", "rental_car_daily"] as const;
const MULTIPLIER_KEYS = ["ten_hour_day_multiplier"] as const;

function orderBy(rates: Rate[], keys: readonly string[]): Rate[] {
  return keys.flatMap((k) => rates.filter((r) => r.key === k));
}

export function RatesTab(): React.ReactElement {
  const api = useAdminApi();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["rates"],
    queryFn: () => api.get<Rate[]>("/api/rates"),
  });
  const mutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: number }) => api.patch<Rate>(`/api/rates/${key}`, { value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rates"] }),
  });

  if (list.isLoading || !list.data) return <LoadingSpinner className="my-12" />;

  const all = list.data;
  const labor = orderBy(all, LABOR_KEYS);
  const perDiem = orderBy(all, PER_DIEM_KEYS);
  const airfare = orderBy(all, AIRFARE_KEYS);
  const driving = orderBy(all, DRIVING_KEYS);
  const multipliers = orderBy(all, MULTIPLIER_KEYS);

  const onChange = (key: string, value: number) => mutation.mutate({ key, value });

  return (
    <div className="flex flex-col gap-4">
      <RatesGroup title="Labor rates" rates={labor} onChange={onChange} />
      <RatesGroup title="Per diem" rates={perDiem} onChange={onChange} />
      <RatesGroup title="Airfare" rates={airfare} onChange={onChange} />
      <RatesGroup title="Driving rates" rates={driving} onChange={onChange} />
      <RatesGroup title="Multipliers" rates={multipliers} onChange={onChange} />
    </div>
  );
}
